/**
 * Екран „Потребители“ (Раздели 8 и 9). Достъпен само за Админ.
 */

import { useState } from 'react';
import {
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USER_ROLES, type UserRole } from '@vidanalyser/shared';
import { apiRequest } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { UserItem } from '../api/types';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Администратор',
  MONITORING: 'Мониторинг',
  MANAGER: 'Ръководител',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [isModalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState({
    email: '',
    fullName: '',
    role: 'MONITORING' as UserRole,
  });

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => apiRequest<{ items: UserItem[] }>('/api/users'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createUser = useMutation({
    mutationFn: () =>
      apiRequest<{ id: string; invited: boolean }>('/api/users', { method: 'POST', body: draft }),
    onSuccess: async (result) => {
      // Профилът се създава и когато писмото не е заминало — поканата може да
      // се повтори, а мълчаливото „създаден" би оставило човека без достъп.
      notifications.show(
        result.invited
          ? { message: `Поканата е изпратена на ${draft.email}`, color: 'green' }
          : {
              message: 'Профилът е създаден, но поканата не замина. Изпратете я повторно.',
              color: 'orange',
            },
      );
      setModalOpen(false);
      setDraft({ email: '', fullName: '', role: 'MONITORING' });
      await invalidate();
    },
    onError: (error: Error) => notifications.show({ message: error.message, color: 'red' }),
  });

  const resendInvite = useMutation({
    mutationFn: (id: string) => apiRequest(`/api/users/${id}/invite`, { method: 'POST' }),
    onSuccess: () => notifications.show({ message: 'Поканата е изпратена отново', color: 'green' }),
    onError: (error: Error) => notifications.show({ message: error.message, color: 'red' }),
  });

  const updateUser = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<UserItem> }) =>
      apiRequest(`/api/users/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: invalidate,
    onError: (error: Error) => notifications.show({ message: error.message, color: 'red' }),
  });

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Потребители</Title>
        <Button onClick={() => setModalOpen(true)}>Нов потребител</Button>
      </Group>

      <Card withBorder p={0}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Име</Table.Th>
              <Table.Th>Имейл</Table.Th>
              <Table.Th w={200}>Роля</Table.Th>
              <Table.Th w={120}>Активен</Table.Th>
              <Table.Th w={150} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(users.data?.items ?? []).map((item) => {
              const isSelf = item.id === currentUser?.id;

              return (
                <Table.Tr key={item.id}>
                  <Table.Td>{item.fullName}</Table.Td>
                  <Table.Td>{item.email}</Table.Td>
                  <Table.Td>
                    <Select
                      data={USER_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
                      value={item.role}
                      disabled={isSelf}
                      onChange={(value) =>
                        value && updateUser.mutate({ id: item.id, patch: { role: value as UserRole } })
                      }
                      allowDeselect={false}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={item.isActive}
                      disabled={isSelf}
                      onChange={(event) =>
                        updateUser.mutate({
                          id: item.id,
                          patch: { isActive: event.currentTarget.checked },
                        })
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    {item.isActive && (
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        loading={resendInvite.isPending && resendInvite.variables === item.id}
                        onClick={() => resendInvite.mutate(item.id)}
                      >
                        Покани отново
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <Text size="sm" c="dimmed">
        Потребителите не се изтриват, а се деактивират — записите им в журнала остават.
      </Text>

      <Modal opened={isModalOpen} onClose={() => setModalOpen(false)} title="Нов потребител">
        <Stack>
          <TextInput
            label="Име"
            value={draft.fullName}
            onChange={(event) => setDraft({ ...draft, fullName: event.currentTarget.value })}
            required
          />
          <TextInput
            label="Имейл"
            type="email"
            value={draft.email}
            onChange={(event) => setDraft({ ...draft, email: event.currentTarget.value })}
            required
          />
          <Select
            label="Роля"
            data={USER_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
            value={draft.role}
            onChange={(value) => value && setDraft({ ...draft, role: value as UserRole })}
            allowDeselect={false}
          />
          <Text size="sm" c="dimmed">
            Паролата си я задава сам потребителят по линк, изпратен на този имейл. Линкът важи
            48 часа.
          </Text>
          <Button loading={createUser.isPending} onClick={() => createUser.mutate()}>
            Създай и покани
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
